// src/components/StickyNoteNode.tsx
import React, { useRef } from 'react';
import { NodeProps, NodeResizer } from '@xyflow/react';
import { useOverflowing } from './useOverflowing';

export interface StickyNoteData {
  text: string;
  color: string;
  linkedEntityId: string | null;
  linkedEntityName: string | null;
  activeFilters: string[];
  isReadonly: boolean;
  // Text is edited in the side panel (opened by clicking the note); kept here so
  // callers that still pass it don't break.
  onTextChange: (id: string, text: string) => void;
  onResizeEnd: (id: string, width: number, height: number) => void;
  onContextMenu: (e: React.MouseEvent, id: string) => void;
  onEntityClick: (entityId: string) => void;
}

const StickyNoteNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  const d = data as unknown as StickyNoteData;
  const displayRef = useRef<HTMLDivElement>(null);

  const isFiltered = d.activeFilters.length > 0 && (
    !d.linkedEntityId || !d.activeFilters.includes(d.linkedEntityId)
  );

  // Whether the text is cut off (drives the fade, the "…" and the tooltip), measured on
  // the text's own box. The fade is drawn beside that box, not inside it: inside, it added
  // 2px of overflow by itself, so a note resized to fit stayed marked as cut off (#1071).
  const isOverflowing = useOverflowing(displayRef, [d.text]);

  return (
    <>
      <NodeResizer
        isVisible={selected && !d.isReadonly}
        minWidth={80}
        minHeight={60}
        handleStyle={{ width: 8, height: 8, background: '#0abfbc', border: 'none', borderRadius: 2 }}
        lineStyle={{ border: '1.5px solid #0abfbc' }}
        onResizeEnd={(_, params) => d.onResizeEnd(id, params.width, params.height)}
      />
      <div
        style={{
          background: d.color,
          borderRadius: 7,
          padding: '8px 10px',
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          boxShadow: selected
            ? '0 0 0 2px #0abfbc, 2px 3px 8px rgba(0,0,0,.18)'
            : '2px 3px 8px rgba(0,0,0,.14)',
          display: 'flex',
          flexDirection: 'column',
          opacity: isFiltered ? 0.3 : 1,
          transition: 'opacity 0.2s',
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          cursor: 'pointer',
        }}
        // A single click opens the edit panel AND selects the note (React Flow), so the
        // resize handles are live at the same time — read the full text in the panel, or
        // resize the note to read it inline. Swallow double-clicks so they don't zoom the canvas.
        onDoubleClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (!d.isReadonly) d.onContextMenu(e, id); }}
      >
        <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <div
            ref={displayRef}
            title={isOverflowing ? d.text : undefined}
            style={{
              flex: 1,
              minHeight: 0,
              fontSize: 12,
              lineHeight: 1.5,
              color: '#222',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              userSelect: 'none',
              cursor: 'pointer',
            }}
          >
            {d.text}
          </div>
          {isOverflowing && (
            // 24px: the two lines' worth it was when it sat inside the 12px text box.
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 24,
              background: `linear-gradient(to bottom, transparent, ${d.color})`,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              paddingRight: 2,
              pointerEvents: 'none',
            }}>
              <span style={{ fontSize: 12, color: '#666', lineHeight: 1 }}>…</span>
            </div>
          )}
        </div>

        {d.linkedEntityId && d.linkedEntityName && (
          <div style={{ marginTop: 6, borderTop: '1px solid rgba(0,0,0,.1)', paddingTop: 5, flexShrink: 0 }}>
            <span
              style={{
                background: '#0abfbc', color: '#fff', borderRadius: 3,
                padding: '2px 6px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                display: 'inline-block',
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                d.onEntityClick(d.linkedEntityId!);
              }}
            >
              {d.linkedEntityName} ↗
            </span>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(StickyNoteNode);
